/**
 * useTriggerBackup — kick off an on-demand backup for any entity from ANYWHERE,
 * recording it in the global backup-activity store so its live status shows up in
 * the universal Activity drawer (and survives a page refresh).
 *
 * Author: Peter Dobler (Dobler Data Solutions) · Feature 201
 */
import { useState } from 'react';
import { assignmentApi, BackupEntityType } from '../api/assignment';
import { useBackupActivity } from '../store/backupActivityStore';

export interface TriggerResult {
  launched: boolean;
  executionName?: string;
  reason?: string;
}

export interface TriggerTarget {
  entityType: BackupEntityType;
  entityId: string;
  name?: string | null;
}

export function useTriggerBackup() {
  const add = useBackupActivity((s) => s.add);
  const [pending, setPending] = useState<Record<string, boolean>>({});

  const trigger = async (t: TriggerTarget, backupType: 'full' | 'incremental' = 'full'): Promise<TriggerResult> => {
    const k = `${t.entityType}:${t.entityId}`;
    setPending((p) => ({ ...p, [k]: true }));
    try {
      const r = await assignmentApi.triggerBackup(t.entityType, t.entityId, backupType);
      if (r.launched && r.executionName) {
        add({ executionName: r.executionName, verb: 'Backup', subject: t.name ?? t.entityId, entityType: t.entityType, entityId: t.entityId });
      }
      return r;
    } catch (e) {
      return { launched: false, reason: (e as Error).message };
    } finally {
      setPending((p) => ({ ...p, [k]: false }));
    }
  };

  return { trigger, pending };
}
